import { t } from '@lingui/macro';
import { FileUploader, UploadContext } from '@remirror/core';
import { FileAttributes, getDefaultFileAttrs } from '@remirror/extension-file';
import AwsS3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import { graphql, useMutation } from 'utils/graphClient';
import { UploadFileHandler_addAttachmentsToPage_Mutation } from './__generated__/UploadFileHandler_addAttachmentsToPage_Mutation.graphql';
import { ImageAttributes } from '@remirror/extension-image';
import useUppyAwsS3UploadParameters, {
  usePresignUploadMutation,
} from 'utils/upload/useUppyAwsS3UploadParameters';
import { UploadFile } from 'utils/upload/types';
import { ONE_GIGABYTE_IN_BYTES } from 'utils/constants';

const addAttachmentsToPageMutation = graphql`
  mutation UploadFileHandler_addAttachmentsToPage_Mutation(
    $pageId: ID!
    $attachments: [PageAttachmentInput!]!
  ) {
    addAttachmentsToPage(
      input: { pageId: $pageId, attachments: $attachments }
    ) {
      attachmentsAdded {
        __typename
        id
        item {
          __typename
          ... on File {
            id
            name
            url
            size
            mimeType
          }
        }
      }
    }
  }
`;

export function useUploadImageHandler({ pageId }: { pageId?: string }) {
  const buildGetUploadParameters = useUppyAwsS3UploadParameters();
  const [addAttachmentsToEvent] =
    useMutation<UploadFileHandler_addAttachmentsToPage_Mutation>(
      addAttachmentsToPageMutation,
    );

  return (
    file: File,
    setProgress: (progress: number) => void,
  ): Promise<ImageAttributes> => {
    return new Promise<ImageAttributes>((resolve, reject) => {
      const uppy = new Uppy({
        meta: { type: 'avatar' },
        debug: true,
        restrictions: {
          maxFileSize: ONE_GIGABYTE_IN_BYTES,
        },
        autoProceed: true,
      });
      const getUploadParameters = buildGetUploadParameters({
        uppy,
        uploadContext: 'FILES',
      });
      uppy.use(AwsS3, {
        getUploadParameters,
      });

      uppy.addFile({
        source: 'editor',
        name: file.name,
        type: file.type,
        data: file,
      });

      uppy.on('progress', (progress) => {
        setProgress(progress);
      });
      uppy.on('complete', () => {
        if (!pageId) {
          return;
        }

        const files: UploadFile[] = uppy.getFiles();
        const fileUploadTokens = files
          .map((f) => f.meta?.uploadToken || '')
          .filter((t) => !!t);
        const fileAttachtments =
          fileUploadTokens?.map((uploadToken) => {
            return { file: { uploadToken } };
          }) || [];

        addAttachmentsToEvent({
          variables: {
            pageId,
            attachments: [...fileAttachtments],
          },
          onCompleted: (data) => {
            if (data.addAttachmentsToPage?.attachmentsAdded.length) {
              const attachmentIds =
                data.addAttachmentsToPage?.attachmentsAdded.map(
                  (attachment) => {
                    return attachment.id;
                  },
                );
              if (
                data.addAttachmentsToPage?.attachmentsAdded[0].item
                  .__typename === 'File'
              ) {
                resolve({
                  fileName: file.name,
                  src: data.addAttachmentsToPage?.attachmentsAdded[0].item.url,
                });
              }
            }
          },
        });
      });
    });
  };
}

export function useUploadFileHandler({ pageId }: { pageId?: string }) {
  const [presignUpload] = usePresignUploadMutation();
  const [addAttachmentsToEvent] =
    useMutation<UploadFileHandler_addAttachmentsToPage_Mutation>(
      addAttachmentsToPageMutation,
    );

  return (): FileUploader<FileAttributes> => {
    let file: File;

    return {
      insert: (f: File) => {
        file = f;
        return getDefaultFileAttrs(f);
      },
      upload: (context: UploadContext) => {
        return new Promise((resolve, reject) => {
          const uppy = new Uppy({
            meta: { type: 'avatar' },
            debug: true,
            restrictions: {
              maxFileSize: ONE_GIGABYTE_IN_BYTES,
            },
            autoProceed: true,
          });
          const getUploadParameters: (file: UppyFile) => any = (file) => {
            return new Promise((resolve, reject) => {
              presignUpload({
                variables: {
                  fileName: file.name,
                  fileType: file.type || '',
                  fileSize: file.size,
                  uploadContext: 'FILES',
                },
                onCompleted: (data) => {
                  if (data.presignUpload) {
                    uppy.setFileMeta(file.id, {
                      uploadToken:
                        data.presignUpload.uploadSignature.uploadToken,
                    });
                    resolve(data.presignUpload.uploadSignature.signature);
                  } else {
                    reject(t`Something went wrong while uploading a file`);
                  }
                },
              });
            });
          };
          uppy.use(AwsS3, {
            getUploadParameters,
          });

          uppy.addFile({
            source: 'editor',
            name: file.name,
            type: file.type,
            data: file,
          });

          uppy.on('progress', (progress) => {
            context.set('loaded', progress);
          });
          uppy.on('complete', () => {
            if (!pageId) {
              return;
            }

            const files: UploadFile[] = uppy.getFiles();
            const fileUploadTokens = files
              .map((f) => f.meta?.uploadToken || '')
              .filter((t) => !!t);
            const fileAttachtments =
              fileUploadTokens?.map((uploadToken) => {
                return { file: { uploadToken } };
              }) || [];

            addAttachmentsToEvent({
              variables: {
                pageId,
                attachments: [...fileAttachtments],
              },
              onCompleted: (data) => {
                if (data.addAttachmentsToPage?.attachmentsAdded.length) {
                  const attachmentIds =
                    data.addAttachmentsToPage?.attachmentsAdded.map(
                      (attachment) => {
                        return attachment.id;
                      },
                    );
                  if (
                    data.addAttachmentsToPage?.attachmentsAdded[0].item
                      .__typename === 'File'
                  ) {
                    resolve({
                      ...getDefaultFileAttrs(file),
                      url: data.addAttachmentsToPage?.attachmentsAdded[0].item
                        .url,
                    });
                  }
                }
              },
            });
          });
        });
      },
      abort: (): void => {},
    };
  };
}
